import { z } from "zod";
import {
  timestampToDateNullable,
  serverStampDate,
} from "@/core/firebase/converters";
import { lenient } from "@/core/zod/lenient";


/**
 * Court document. Stored at `clubs/{clubId}/courts/{courtId}`.
 * See model-change-checklist.md before adding or renaming fields.
 */

export const COURT_LOCATION = ["indoor", "outdoor", "covered"] as const;
export const CourtLocationSchema = z.enum(COURT_LOCATION);
export type CourtLocation = z.infer<typeof CourtLocationSchema>;

export const COURT_WALL_TYPE = [
  "crystal",
  "wall",
  "panoramic",
  "mixed",
] as const;
export const CourtWallTypeSchema = z.enum(COURT_WALL_TYPE);
export type CourtWallType = z.infer<typeof CourtWallTypeSchema>;

export const COURT_FLOOR_SURFACE = [
  "artificialTurf",
  "concrete",
  "synthetic",
] as const;
export const CourtFloorSurfaceSchema = z.enum(COURT_FLOOR_SURFACE);
export type CourtFloorSurface = z.infer<typeof CourtFloorSurfaceSchema>;

export const COURT_SIZE = ["normal", "small"] as const;
export const CourtSizeSchema = z.enum(COURT_SIZE);
export type CourtSize = z.infer<typeof CourtSizeSchema>;

/**
 * Sport played on the court. Multi-sport-READY schema — the product stays
 * padel-only for now (only `padel` is offered/seeded), but carrying the
 * dimension means adding pickleball/tennis later is an enum value + UI, not a
 * data migration. Lenient → 'padel' so every existing court reads correctly.
 */
export const COURT_SPORT = ["padel"] as const;
export const CourtSportSchema = z.enum(COURT_SPORT);
export type CourtSport = z.infer<typeof CourtSportSchema>;
export const COURT_SPORT_LABEL: Record<CourtSport, string> = {
  padel: "Pádel",
};

/**
 * Court operational status — single source of truth for "what state is
 * this court in right now?". Replaces the prior `isActive: boolean`
 * which couldn't express maintenance as a distinct state.
 *
 *   active      — bookable, shows normally on the schedule.
 *   maintenance — visible on the schedule (so reception sees the row)
 *                 but striped + labelled "Mantenimiento". Excluded
 *                 from the "Pistas libres" count. Slot click still
 *                 works (an owner can override).
 *   inactive    — fully hidden from the schedule. Use for retired
 *                 / decommissioned courts.
 *
 * Future statuses (e.g. `reserved`, `cleaning`) extend this enum
 * without touching binary boolean fields scattered across the app.
 */
export const COURT_STATUS = ["active", "maintenance", "inactive"] as const;
export const CourtStatusSchema = z.enum(COURT_STATUS);
export type CourtStatus = z.infer<typeof CourtStatusSchema>;
export const COURT_STATUS_LABEL: Record<CourtStatus, string> = {
  active: "Activa",
  maintenance: "Mantenimiento",
  inactive: "Inactiva",
};

export const PRICING_MODE = ["perHour", "perSlot"] as const;
export const PricingModeSchema = z.enum(PRICING_MODE);
export type PricingMode = z.infer<typeof PricingModeSchema>;

/**
 * Suffix shown after a price to convey its charge basis: `/h` for hourly
 * (`perHour`) rules, `/reserva` for per-slot (`perSlot`) rules. Single source
 * so the Pistas list (desktop cell + mobile card) and the court drawer can't
 * drift — the mobile card used to hardcode `/h`, mislabelling a per-slot
 * price as hourly. `undefined` (no rule) falls back to `/reserva`.
 */
export function pricingUnitLabel(mode: PricingMode | null | undefined): string {
  return mode === "perHour" ? "/h" : "/reserva";
}

export const PricingRuleSchema = z.object({
  id: z.string(),
  daysOfWeek: z.array(z.number().int().min(1).max(7)),
  startTime: z.string().default("00:00"),
  endTime: z.string().default("23:59"),
  price: z.number().nonnegative().default(0),
  pricingMode: PricingModeSchema.default("perHour"),
  priority: z.number().int().default(0),
  /** Optional seasonal window: the rule applies only on dates within
   *  [startDate, endDate] inclusive ('YYYY-MM-DD'). Null on either side ⇒
   *  open-ended on that side; both null (the default) ⇒ always-on, i.e. a
   *  non-seasonal rule (unchanged behaviour). Lets a club run summer / winter
   *  / promo pricing without a schema change. Optional everywhere — omit both
   *  for an all-year rule (the default, unchanged behaviour). */
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
});
export type PricingRule = z.infer<typeof PricingRuleSchema>;

/**
 * Pricing rules are stored as a Map<id, rule> in Firestore (not an array).
 * This preprocess flattens the map into the array we want to work with in TS.
 */
const pricingRulesMapToArray = z.preprocess((value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).map(([id, rule]) => ({
      id,
      ...(rule as Record<string, unknown>),
    }));
  }
  return [];
}, z.array(PricingRuleSchema));

export const CourtSchema = z.object({
  id: z.string(),
  /** Denormalised clubId — redundant with the doc's path
   * (`clubs/{clubId}/courts/{courtId}`); kept for queries + audit. */
  clubId: z.string(),
  name: z.string().default(""),
  // Lenient on cosmetic enum attributes — an unknown value (legacy doc,
  // future enum addition) shouldn't make the court vanish from the
  // schedule grid. Wrapper logs drift; `.default()` covers missing-field.
  // Strict validation lives on the court edit form.
  location: lenient(CourtLocationSchema, 'indoor', 'court.location').default('indoor'),
  wallType: lenient(CourtWallTypeSchema, 'crystal', 'court.wallType').default('crystal'),
  floorSurface: lenient(CourtFloorSurfaceSchema, 'artificialTurf', 'court.floorSurface').default('artificialTurf'),
  hasLighting: z.boolean().default(true),
  size: lenient(CourtSizeSchema, 'normal', 'court.size').default('normal'),
  /** Sport played. Padel-only today; schema carries the dimension so other
   *  sports are an enum value + UI later, not a migration. */
  sport: lenient(CourtSportSchema, 'padel', 'court.sport').default('padel'),
  /** Explicit player capacity. Optional — absent ⇒ size-derived default
   *  (see `effectiveCourtCapacity`). Lets a club set a real per-court
   *  capacity and makes non-4 sports possible later without a schema change. */
  capacity: z.number().int().positive().nullable().optional(),
   /**
   * Operational status. Replaced the original `isActive: boolean` pre-launch
   * (all docs backfilled to `status` 2026-05-31 — see next-prod-release.md;
   * the CF `isActive` fallback was dropped at the same time). The `lenient`
   * wrapper + `.default('active')` is purely defensive now: an unknown or
   * missing value falls back to 'active' (safe-visible) and logs drift — it
   * is NOT a legacy mapper.
   */
  status: lenient(CourtStatusSchema, 'active', 'court.status').default('active'),
  sortOrder: z.number().int().default(0),
  pricingRules: pricingRulesMapToArray.default([]),
  maintenanceStart: timestampToDateNullable.nullable().optional(),
  maintenanceEnd: timestampToDateNullable.nullable().optional(),
  /** UID of the staff member who last edited this court (name, pricing, status).
   *  Optional + nullable — absent on docs written before this field was added;
   *  stamped on every updateCourt / updatePricingRules write thereafter. */
  updatedByUid: z.string().nullable().optional(),
  createdAt: serverStampDate,
  updatedAt: serverStampDate,
});

export type Court = z.infer<typeof CourtSchema>;

/** Default capacity from court size — `CourtModel.capacity`. */
export function courtCapacity(size: CourtSize): number {
  return size === "small" ? 2 : 4;
}

/**
 * The court's effective player capacity: the explicit `capacity` when set,
 * else the size-derived default. Use this everywhere capacity is needed
 * (roster cap, per-player split) so a club's explicit value wins and future
 * non-4 sports work without touching every call site.
 */
export function effectiveCourtCapacity(
  court: Pick<Court, "capacity" | "size">,
): number {
  return court.capacity ?? courtCapacity(court.size);
}

/**
 * True if the court is currently in maintenance — either because the
 * operator explicitly toggled `status: 'maintenance'` (the receptionist
 * "broken net, take it out now" path) OR because today falls inside a
 * scheduled `maintenanceStart`–`maintenanceEnd` window (the owner
 * "court resurfacing next week" path). Both paths feed the same
 * downstream behaviour (striped row + excluded from free-court count).
 */
export function isCourtUnderMaintenance(
  court: Court,
  today = new Date(),
): boolean {
  if (court.status === "maintenance") return true;
  if (!court.maintenanceStart || !court.maintenanceEnd) return false;
  const dayMs = 86_400_000;
  const todayDay = Math.floor(today.getTime() / dayMs);
  const startDay = Math.floor(court.maintenanceStart.getTime() / dayMs);
  const endDay = Math.floor(court.maintenanceEnd.getTime() / dayMs);
  return todayDay >= startDay && todayDay <= endDay;
}

/**
 * Whether the court should appear on the schedule grid at all. Courts
 * with `status: 'inactive'` are fully hidden (decommissioned).
 * `active` and `maintenance` both show — the second with the striped
 * indicator. Replaces the prior `c.isActive` checks scattered across
 * the app.
 */
export function isCourtVisibleOnSchedule(court: Court): boolean {
  return court.status !== "inactive";
}

/**
 * Whether a court may be the *default* target a new-booking form auto-picks.
 * Only fully `active` courts qualify. Maintenance courts stay selectable in
 * the dropdown (the deliberate "book the broken court anyway" path) but are
 * never the silent default — opening the form on a maintenance court forces
 * reception to correct the court before saving. Distinct from
 * `isCourtVisibleOnSchedule` (keeps maintenance, for the grid) and
 * `isCourtUnderMaintenance` (today-window aware).
 */
export function isCourtBookable(court: Court): boolean {
  return court.status === "active";
}

/**
 * Mermaid source rendering the pricing-rule resolution flow used by
 * `features/bookings/domain/pricing.ts#priceFor()`. Kept here (next to the
 * `PricingRule` schema) so the Help registry (in `core/`) can import it
 * without crossing feature boundaries. If you touch the resolver, refresh
 * the diagram in the same diff.
 */
export const PRICING_PRECEDENCE_DIAGRAM = `
flowchart TD
  A[Nueva reserva:<br/>pista, fecha/hora, duración] --> B{¿La pista tiene<br/>reglas propias?}
  B -- sí --> C[Buscar regla del día+hora+fecha<br/>con mayor prioridad]
  C --> D{¿Alguna coincide?}
  D -- sí --> Court[Precio de la pista]
  D -- no --> E{¿Club tiene<br/>reglas por defecto?}
  B -- no --> E
  E -- sí --> F[Buscar regla del día+hora+fecha<br/>con mayor prioridad]
  F --> G{¿Alguna coincide?}
  G -- sí --> Club[Precio por defecto del club]
  G -- no --> None[Sin precio — dejar vacío]
  E -- no --> None
  classDef win fill:#1e4026,stroke:#3cb371,color:#d4ffe4;
  classDef none fill:#3a3a3a,stroke:#777,color:#ccc;
  class Court,Club win;
  class None none;
`.trim();
// Note: "día+hora+fecha" = día de la semana + franja horaria + ventana estacional (startDate/endDate).
